<template>
  <FilterComponent :open="!!open" @change:state="(value) => $emit('change:state', value)"
    :fixed="true">
    <v-layout row justify-space-between>
      <div class="font-weight-thin text-uppercase title pa-3">{{ $t('Filter') }}</div>
      <div>
        <v-btn color="accent" @click="applyFilter" dark>{{ $t('APPLY') }}</v-btn>
      </div>
    </v-layout>
    <v-divider class="mb-2"></v-divider>
    <v-flex xs12>
      <v-expansion-panel v-model="panel" expand focusable>
        <v-expansion-panel-content class="secondary mb-1" v-for="exp in currentFilter"
          :key="exp.category">
          <template v-slot:header>
            <div>{{ $t(exp.category) }}</div>
          </template>
          <Card class="pa-3" :key="forceReloadFilter">
            <div v-for="filter in exp.children" :key="filter.key">
              <div v-if="filter.value && filter.type == 'select'">
                <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
                <v-select :items="selectRange(filter)" :value='activeParameters[`${filter.id}`]'
                  @change="onChangeHandler($event, filter)" :item-text="(options) => $t(options.text)"
                  :prepend-inner-icon="prependIcon(filter)" return-object class="white--text" color="accent"
                  :outline="outline(filter)" :solo="outline(filter)" :label="$t(filter.title)">
                </v-select>
              </div>
              <div v-if="filter.value && filter.type == 'selectCombo'">
                <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
                <v-select :items="selectRange(filter)"
                  :value='activeParameters[`${filter.id}`]'
                  @change='onChangeHandler($event, filter)'
                  class="white--text"
                  return-object
                  :outline="outline(filter)" :solo="outline(filter)" :label="$t(filter.title)"
                  :item-text="(options) => $t(options.text)" item-value="modifiedId"
                  multiple
                  required>
                  <template slot="prepend-item">
                    <v-list-tile ripple @click="toggleSelectAll(filter)">
                      <v-list-tile-action>
                        <v-icon color="indigo darken-4">mdi-checkbox-blank-outline</v-icon>
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title v-if="activeParameters[`${filter.id}`].length == selectRange(filter).length">{{ $t('Deselect All') }}
                        </v-list-tile-title>
                        <v-list-tile-title v-else>{{ $t('Select All') }}</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-divider class="mt-2"></v-divider>
                  </template>

                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0">
                      {{ item.text }}
                    </span>
                    <span v-if="index === 1" class="grey--text caption">(+{{ activeParameters[`${filter.id}`].length - 1 }}
                      {{ $t('others') }})</span>
                  </template>
                </v-select>
              </div>

              <div v-else-if="filter.type == 'datePicker'">
                <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
                <RangeDatePicker
                  @change:date="onChangeHandler($event, filter)"
                  :fullPicker="true"
                  :from="activeParameters[`${filter.id}`]['from']"
                  :to="activeParameters[`${filter.id}`]['to']"
                  :span="dateRangeAllowed"
                  :minDate="minimumDateAllowed">
                </RangeDatePicker>
              </div>
              <div v-else-if="filter.type == 'singleDatePicker'">
                <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
                <RangeDatePicker :from="activeParameters[`${filter.id}`]['from']"
                  :to="activeParameters[`${filter.id}`]['to']"
                  @change:date="onChangeHandler($event, filter)" :fullPicker="false" :minDate="minimumDateAllowed">
                </RangeDatePicker>
              </div>
              <div v-else-if="filter.type == 'timePicker'">
                <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
                <RangeTimePicker @change:time="(val) => onChangeHandler(val, filter)"
                  :fullPickerWidth="true">
                </RangeTimePicker>
              </div>
              <div v-else-if="filter.type == 'checkbox'">
                <v-checkbox v-model="activeParameters[`${filter.id}`]" :label="$t(filter.title)">
                </v-checkbox>
              </div>
              <div v-else-if="filter.type == 'switch'">
                <CustomToggle :labelLeftText="labelLeftText" :labelRightText="labelRightText">
                </CustomToggle>
              </div>
            </div>
          </Card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-flex>
  </FilterComponent>
</template>

<script>
import { miscellaneous } from '@/util/mixin/index'
import { mapGetters, mapMutations } from 'vuex'
import _ from 'lodash'
import moment from 'moment'

export default {
  mixins: [miscellaneous],
  props: {
    filterProps: Array,
    active: Object,
    open: {
      default: false,
      type: Boolean
    }
  },
  components: {
    FilterComponent: () => import('@/components/Filter/FilterContainer'),
    RangeDatePicker: () =>
      import('@/components/utility/RangeDatePicker.vue'),
    RangeTimePicker: () =>
      import('@/components/utility/RangeTimePicker.vue')
  },
  data() {
    return {
      forceReloadFilter: 0,
      panel: [true],
      currentFilter: {},
      activeParameters: {},
      error: {},
      dateRangeAllowed: 31,
      minimumDateAllowed: ''
    }
  },
  computed: {
    ...mapGetters(['isUpdateFilterDb', 'getDefaultDate']),
    currentRouteName() {
      return this.$route.name;
    }
  },
  mounted() {
    this.activeParameters = _.cloneDeep(this.active)
    this.currentFilter = this.filterProps.map((exp) => {
      let realExp = _.cloneDeep(exp)
      realExp.children = realExp.children.filter(
        (child) =>
          child.condition
            ? child.condition(this.activeParameters[child.ref])
            : true
      )

      return realExp
    })
    if (this.currentRouteName == 'custom-report' && this.activeParameters['Date']) {
      if (
        this.activeParameters['selectedRange'].intervalGap &&
        this.activeParameters['selectedRange'].intervalGap == 1
      ) {
        this.minimumDateAllowed = moment().subtract(90, 'day').format('YYYY-MM-DD')
        this.dateRangeAllowed = 1
      } else if (
        this.activeParameters['selectedRange'].intervalGap &&
        this.activeParameters['selectedRange'].intervalGap == 5
      ) {
        this.minimumDateAllowed = ''
        this.dateRangeAllowed = 15
      } else {
        this.minimumDateAllowed = ''
        this.dateRangeAllowed = moment(this.activeParameters['Date']['from']).endOf('month').format('DD')
      }
    } else if (this.currentRouteName == 'plantAnalytics') {
      if (
        this.activeParameters['selectedInterval'].value &&
        this.activeParameters['selectedInterval'].value == 1
      ) {
        this.minimumDateAllowed = moment().subtract(90, 'day').format('YYYY-MM-DD')
        this.dateRangeAllowed = 10
      } else if (
        this.activeParameters['selectedInterval'].value &&
        this.activeParameters['selectedInterval'].value == 5
      ) {
        this.minimumDateAllowed = ''
        this.dateRangeAllowed = 15
      } else {
        this.minimumDateAllowed = ''
        this.dateRangeAllowed = moment(this.activeParameters['Date']['from']).endOf('month').format('DD')
      }
    }
  },
  methods: {
    ...mapMutations(['SET_UPDATE_FILTER_DB']),
    selectRange(filter) {
      if (filter.value && typeof filter.value === 'function') {
        return filter['value'](this.activeParameters[filter.ref])
      } else {
        return filter.value
      }
    },
    outline(filter) {
      return filter && filter.attr && filter.attr.outline
        ? filter.attr.outline
        : true
    },
    prependIcon(filter) {
      return this.activeParameters[`${filter.title}`] &&
        this.activeParameters[`${filter.title}`].icon
        ? this.activeParameters[`${filter.title}`].icon
        : ''
    },
    isVisible(filter, activeParameters) {
      return filter.condition ? filter.condition(activeParameters) : true
    },
    applyFilter() {
      this.SET_UPDATE_FILTER_DB(true)
      this.$emit('changeFilterOption', this.activeParameters)
    },
    onChangeHandler(value, filter) {
      if (filter.id instanceof Object) {
        this.$set(this.activeParameters[filter.id], 'from', value.from)
        this.$set(this.activeParameters[filter.id], 'to', value.to)
      } else {
        this.$set(this.activeParameters, filter.id, value)
      }

      if(this.currentRouteName == 'custom-report') {
        if(this.activeParameters['selectedRange'].intervalGap && this.activeParameters['selectedRange'].intervalGap > 5) {
          this.minimumDateAllowed = ''
          this.dateRangeAllowed = moment(this.activeParameters['Date']['from']).endOf('month').format('DD')
        }
      } else if(this.currentRouteName == 'plantAnalytics') {
        if(this.activeParameters['selectedInterval'].value && this.activeParameters['selectedInterval'].value > 5) {
          this.minimumDateAllowed = ''
          this.dateRangeAllowed = moment(this.activeParameters['Date']['from']).endOf('month').format('DD')
        }
      } else {
        this.minimumDateAllowed = ''
        this.dateRangeAllowed = moment(this.activeParameters['Date']['from']).endOf('month').format('DD')
      }

      this.currentFilter = this.filterProps.map((realExp) => {
        let exp = _.cloneDeep(realExp)
        exp.children = exp.children.filter((child) => {
          if (child.condition) {
            return child.condition(this.activeParameters[child.ref])
          } else {
            return true
          }
        })
        exp.children.forEach((child) => {
          if (filter.id === child.ref) {
            if (child.type === 'select') {
              let value = this.selectRange(child)
              this.$set(this.activeParameters, child.id, value[0])
              if (this.currentRouteName == 'custom-report' && this.activeParameters['Date']) {
                if (
                  this.activeParameters['selectedRange'].intervalGap &&
                  this.activeParameters['selectedRange'].intervalGap == 1
                ) {
                  this.minimumDateAllowed = moment().subtract(90, 'day').format('YYYY-MM-DD')
                } else if (
                  this.activeParameters['selectedRange'].intervalGap &&
                  this.activeParameters['selectedRange'].intervalGap == 5
                ) {
                  this.minimumDateAllowed = ''
                  this.dateRangeAllowed = 15
                } else {
                  this.minimumDateAllowed = ''
                  this.dateRangeAllowed = moment(this.activeParameters['Date']['from']).endOf('month').format('DD')
                }
              } else if (this.currentRouteName == 'plantAnalytics') {
                if (
                  this.activeParameters['selectedInterval'].value &&
                  this.activeParameters['selectedInterval'].value == 1
                ) {
                  this.minimumDateAllowed = moment().subtract(90, 'day').format('YYYY-MM-DD')
                  this.dateRangeAllowed = 10
                } else if (
                  this.activeParameters['selectedInterval'].value &&
                  this.activeParameters['selectedInterval'].value == 5
                ) {
                  this.minimumDateAllowed = ''
                  this.dateRangeAllowed = 15
                } else {
                  this.minimumDateAllowed = ''
                  this.dateRangeAllowed = moment(this.activeParameters['Date']['from']).endOf('month').format('DD')
                }
              }
            } else if (
              child.type === 'datePicker' ||
              child.type === 'singleDatePicker'
            ) {
              let currentDateObj = {
                from: this.getDefaultDate,
                to: this.getDefaultDate
              }
              this.$set(this.activeParameters, child.id, currentDateObj)
            } else if (child.type === 'selectCombo') {
              let value = this.selectRange(child)
              this.$set(this.activeParameters, child.id, _.cloneDeep(value))
            }
          }
        })

        return exp
      })
    },
    toggleSelectAll(filter) {
      if (this.activeParameters[`${filter.id}`].length === this.selectRange(filter).length) {
        this.activeParameters[`${filter.id}`] = []
      } else {
        this.activeParameters[`${filter.id}`] = _.cloneDeep(this.active[filter.id])
      }
    }
  },
  watch: {
    active() {
      this.activeParameters = _.cloneDeep(this.active)
      this.forceReloadFilter += 1
    },
    activeParameters() {
      Object.keys(this.activeParameters).forEach((item) => {
        this.$watch('activeParameters.' + item, function() {
          this.filterProps.forEach((realExp) => {
            let exp = _.cloneDeep(realExp)
            exp.children = exp.children.filter((child) => {
              if (child.id === item && child.superPower) {
                const obj = {
                  key: item,
                  value: this.activeParameters[item]
                }
                this.SET_UPDATE_FILTER_DB(false)
                this.$emit('updateFilter', obj)
              } else if (child.ref === item) {
                if (
                  Array.isArray(this.active[child.id]) &&
                  this.active[child.id].length > 1
                ) {
                  if (child.type === 'select') {
                    this.$set(this.activeParameters, child.id, [
                      this.active[child.id][0]
                    ])
                  } else if (child.type === 'datePicker' || child.type === 'singleDatePicker' && !this.active[child.id]) {
                    let currentDateObj = {
                      from: this.getDefaultDate,
                      to: this.getDefaultDate
                    }
                    this.$set(this.activeParameters, child.id, currentDateObj)
                  } else {
                    this.$set(this.activeParameters, child.id, _.cloneDeep(this.active[child.id]))
                  }
                } else {
                  if (child.type === 'datePicker' || child.type === 'singleDatePicker' && !this.active[child.id]) {
                    let currentDateObj = {
                      from: this.getDefaultDate,
                      to: this.getDefaultDate
                    }
                    this.$set(this.activeParameters, child.id, currentDateObj)
                  } else {
                    this.$set(
                      this.activeParameters,
                      child.id,
                      this.active[child.id]
                    )
                  }
                }
              }
            })
          })
        })
      })
    },
    filterProps() {
      // TODO: Decouple value and the actual configuration
      this.currentFilter = this.filterProps.map((item, index) => {
        let exp = _.cloneDeep(item)
        exp.children = exp.children.filter((child) => {
          if (child.condition) {
            return child.condition(this.activeParameters[child.ref])
          } else {
            return true
          }
        })

        let changedAttrValue = exp.children.filter((el, i) => {
          if (!this.currentFilter[index].children[i]) {
            return false
          }
          return !this.isObjectEqual(el, this.currentFilter[index].children[i])
        })
        exp.children.forEach((child) => {
          if (changedAttrValue.length == 1) {
            const isSthChanged = changedAttrValue[0].id == child.id
            if (isSthChanged && child.type === 'select') {
              let value = this.selectRange(child)
              this.$set(this.activeParameters, child.id, value[0])
            } else if (
              (isSthChanged && child.type === 'datePicker') ||
              child.type === 'singleDatePicker'
            ) {
              let currentDate = this.getDefaultDate
              this.$set(this.activeParameters, child.id, {
                from: currentDate,
                to: currentDate
              })
            } else if (isSthChanged && child.type === 'selectCombo') {
              let value = this.selectRange(child)
              this.$set(this.activeParameters, child.id, _.cloneDeep(value))
            }
          } else {
            if (child.type === 'select') {
              let value = this.selectRange(child)
              this.$set(this.activeParameters, child.id, value[0])
            } else if (
              child.type === 'datePicker' ||
              child.type === 'singleDatePicker'
            ) {
              let currentDate = this.getDefaultDate
              this.$set(this.activeParameters, child.id, {
                from: currentDate,
                to: currentDate
              })
            } else if (child.type === 'selectCombo') {
              let value = this.selectRange(child)
              this.$set(this.activeParameters, child.id, _.cloneDeep(value))
            }
          }
        })
        return exp
      })
      if (this.isUpdateFilterDb) {
        this.applyFilter()
      }
    }
  }
}
</script>

<style scoped>
.combobox--overflow {
  min-height: 60px;
  max-height: 60px;
  overflow-x: hidden;
  overflow-y: auto;
}

.rangeError {
  color: #c04d53;
}
</style>
